/**
 * WHICH BUTTON SUBMITTED A DIALOG FORM — the one question every dialog in this
 * console has to ask, and until now almost none of them did.
 *
 * ## The defect this exists to close
 *
 * Every dialog here is a `<form method="dialog">`, and **both** its buttons are
 * `type="submit"`: the commit carries `value="save"` and the exit carries
 * `value="cancel"`. That is deliberate — a `method="dialog"` submit is what
 * closes the native `<dialog>` and sets its `returnValue`, and `formNoValidate`
 * on the exit is what stops the browser demanding a value from an operator who
 * has just decided not to supply one.
 *
 * What was missing is that a form's `onSubmit` fires for **either** button.
 * Nine dialogs called `handleSubmit(onSubmit)` from there unconditionally, so
 * pressing «ألغِ» validated the form and dispatched the write. Cancelling a
 * dialog CREATED the thing it was cancelling: a shelf, a plan, a gateway, an
 * entitlement, a rotated secret. It reached the console as «I closed that
 * without saving and it saved anyway», which is the worst shape a bug can take
 * on a surface whose whole job is deliberate operator action.
 *
 * `onClick={onClose}` on the exit did not prevent it. The click handler runs,
 * the parent sets `open` to false — and the submit event still runs to
 * completion on the form that is on its way out.
 *
 * ## The rule
 *
 * A dialog form commits **only** when its own commit button submitted it, and
 * every other submitter is left alone to close the dialog natively.
 *
 * Returning early rather than preventing the default is the second half: with
 * the default intact, `method="dialog"` closes the element and sets
 * `returnValue`, which is exactly what the exit path is supposed to do. The
 * commit path still calls `preventDefault()`, so a refused write keeps the
 * operator's values on screen instead of discarding them.
 */

/** The value every dialog's exit button carries. */
export const CANCEL_SUBMITTER = 'cancel';

/**
 * Whether this submit came from a button OTHER than the named commit.
 *
 * `submitter` is `null` when a form is submitted programmatically or by pressing
 * Enter in a field with no default button. That is an implicit commit — the
 * reader is in the form, typing — so it is treated as one rather than swallowed.
 */
export function isCancelledSubmit(event: React.FormEvent<HTMLFormElement>, commitValue = 'save'): boolean {
  const { submitter } = event.nativeEvent as SubmitEvent;
  if (!(submitter instanceof HTMLButtonElement)) return false;
  return submitter.value !== commitValue;
}
